import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  configurationQuery,
  hostLink,
  type ConfigCard,
  type ConfigGroup,
  type DeviceConfigurationPage,
  type HostLinkAction,
  type Hosting,
} from '../api/devices'
import { Spinner } from '../ui/Spinner'
import { SelectPicker } from '../ui/SelectPicker'
import { toast } from '../ui/notify'
import type { ScreenProps } from './screens'
import { DeviceLocalProfiles } from './DeviceLocalProfiles'

/**
 * Fila Configuration a fisei - inventoree/devices/others/deviceDetails.php cu
 * gesturile din device_workspace.js: cartela gazdei pe un rand (felul, o bara,
 * lista) si cartela masinilor virtuale cu plusul ei, apoi grupurile de piese
 * ale cutiei - un patrat pe fel de componenta, cu iconita, numele, numarul cand
 * sunt mai multe, randul care o identifica si trei proprietati; restul sub
 * chevron (`is-open`). Salvarea legaturii e pe loc: alegi gazda sau apesi
 * plus, se scrie, si cartelele se refac din nou de la server, cum facea
 * `hostingPost` cu `.device-hosting`.
 */

const DEVICE_HREF = (id: number) => `/inventoree/device/${id}`

export function DeviceConfiguration({ id }: ScreenProps) {
  const { data, error } = useQuery(configurationQuery(id))

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  return <Configuration page={data} />
}

function Configuration({ page }: { page: DeviceConfigurationPage }) {
  return (
    <>
      {page.hosting && <HostingCards id={page.device.id} hosting={page.hosting} />}
      {page.groups.length > 0 ? (
        <div className="dcol-grid dcol-grid-span">
          {page.groups.map((group) => (
            <Group key={group.key} group={group} />
          ))}
        </div>
      ) : (
        <div className="alert alert-info">
          <i className="fas fa-info-circle"></i> No hardware specifications for this device.
        </div>
      )}
      <DeviceLocalProfiles id={page.device.id} />
    </>
  )
}

/* ------------------------------------------------------------------------- */
/* gazda si masinile virtuale                                                */
/* ------------------------------------------------------------------------- */

function HostingCards({ id, hosting }: { id: number; hosting: Hosting }) {
  const client = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)

  /* hostingPost: se scrie, apoi cartelele se aduc din nou; la refuz, toastr-ul
     spune motivul in cuvintele serverului. Cartelele refacute vin cu alegatorul
     de VM-uri inchis, ca markupul proaspat din portal. */
  const post = async (payload: HostLinkAction) => {
    setBusy(true)
    try {
      await hostLink(id, payload)
      setPickerOpen(false)
      await client.invalidateQueries({ queryKey: ['device', id, 'configuration'] })
    } catch (err) {
      toast(err instanceof Error && err.message !== '' ? err.message : 'Please try again.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  const s = hosting.suggested

  return (
    <div className="device-hosting" data-device={id} data-url={`/api/v2/devices/${id}/host-link`}>
      {hosting.showHost && (
        <section className="dcf-card dcf-host-card">
          <div className="dcf-oneline">
            <span className="dcf-icon">
              <i className="fas fa-server" aria-hidden="true"></i>
            </span>
            <span className="dcf-host-type">{hosting.typeLabel}</span>
            <span className="dcf-sep" aria-hidden="true">
              |
            </span>
            <label className="dcf-inline-label" htmlFor={`dcf-host-${id}`}>
              Host
            </label>
            <SelectPicker
              id={`dcf-host-${id}`}
              className="form-control js-host-select"
              title="Choose the inventoried machine this one runs on"
              disabled={busy}
              value={String(hosting.hostId)}
              options={[{ value: '0', label: 'Not linked' }, ...hosting.choices.map((c) => ({ value: String(c.id), label: c.name }))]}
              onChange={(v) => void post({ action: 'set-host', host_device_id: Number(v) || 0 })}
            />
            {hosting.canOpenHost && (
              <a className="dcf-host-open" href={DEVICE_HREF(hosting.hostId)} title={`Open ${hosting.hostName}`}>
                <i className="fas fa-external-link-alt" aria-hidden="true"></i>
              </a>
            )}
          </div>

          {hosting.guessedBy !== '' && (
            <p className="dcf-host-hint dcf-host-hint-soft">
              <i className="fas fa-magic" aria-hidden="true"></i>{' '}
              {hosting.guessedBy === 'gateway'
                ? 'Linked automatically: the only physical server on the same gateway.'
                : 'Linked automatically, from what discovery saw on the network.'}{' '}
              Change it above if it is wrong.
            </p>
          )}

          {s && (
            <p className="dcf-host-hint">
              <i className="fas fa-lightbulb" aria-hidden="true"></i>{' '}
              {s.others === 0 ? (
                <>
                  Probably runs on <strong>{s.name}</strong> - the only physical server on the same gateway ({s.gateway}).
                </>
              ) : (
                <>
                  Could run on <strong>{s.name}</strong> - one of {s.others + 1} physical servers on the same gateway ({s.gateway}).
                </>
              )}{' '}
              <button
                type="button"
                className="dcf-host-apply js-host-apply"
                data-id={s.id}
                disabled={busy}
                onClick={() => void post({ action: 'set-host', host_device_id: s.id })}
              >
                Link it
              </button>
            </p>
          )}
        </section>
      )}

      {hosting.showVms && (
        <section className="dcf-card dcf-host-card">
          <header className="dcf-head dcf-head-static">
            <span className="dcf-icon">
              <i className="fas fa-cubes" aria-hidden="true"></i>
            </span>
            <span className="dcf-title">Virtual Machines</span>
            {hosting.vmCount > 0 && <span className="dcf-count">{hosting.vmCount}</span>}
            <button
              type="button"
              className="dcf-plus js-toggle-guest-picker"
              title="Add a virtual machine"
              aria-label="Add a virtual machine"
              onClick={(ev) => {
                ev.stopPropagation()
                setPickerOpen(!pickerOpen)
              }}
            >
              <i className="fas fa-plus" aria-hidden="true"></i>
            </button>
          </header>
          <div className="dcf-summary">
            {hosting.vmCount === 0 && <div className="dcf-line dcf-muted">None linked yet.</div>}
            <ul className="dcf-guest-list">
              {hosting.guests.map((g) => (
                <li key={g.id}>
                  <a href={DEVICE_HREF(g.id)}>{g.name}</a>
                  {(g.state !== '' || g.memory !== '') && (
                    <span className="dcf-vm-meta">
                      {g.state !== '' && <span className={`dcf-vm-state dcf-vm-state-${g.stateKey}`}>{g.state}</span>}
                      {g.memory !== '' && <span className="dcf-vm-ram">{g.memory}</span>}
                    </span>
                  )}
                  <button
                    type="button"
                    className="dcf-unlink js-unlink-guest"
                    data-id={g.id}
                    title="Remove"
                    aria-label="Remove"
                    disabled={busy}
                    onClick={() => void post({ action: 'remove-guest', guest_id: g.id })}
                  >
                    <i className="fas fa-times" aria-hidden="true"></i>
                  </button>
                </li>
              ))}
              {hosting.unresolved.map((hv) => (
                <li key={hv.name} className="dcf-guest-unresolved">
                  <span className="dcf-guest-name">{hv.name}</span>
                  <span className="dcf-vm-meta">
                    {hv.state !== '' && <span className={`dcf-vm-state dcf-vm-state-${hv.stateKey}`}>{hv.state}</span>}
                    {hv.memory !== '' && <span className="dcf-vm-ram">{hv.memory}</span>}
                    {hv.suggestedId > 0 ? (
                      <>
                        <span className="dcf-vm-hint">UUID/MAC match {hv.suggestedName}</span>
                        <button
                          type="button"
                          className="dcf-host-apply js-guest-apply"
                          data-id={hv.suggestedId}
                          disabled={busy}
                          onClick={() => void post({ action: 'add-guest', guest_id: hv.suggestedId })}
                        >
                          Link it
                        </button>
                      </>
                    ) : (
                      <span className="dcf-vm-hint">Not in fleet</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            <div className="dcf-picker js-guest-picker" hidden={!pickerOpen}>
              <label className="dcf-pick-label" htmlFor={`dcf-guest-${id}`}>
                Add machine
              </label>
              <SelectPicker
                id={`dcf-guest-${id}`}
                className="form-control js-guest-select"
                title="Choose a machine that runs here"
                disabled={busy}
                value=""
                options={[{ value: '', label: 'Choose a machine…' }, ...hosting.pickerChoices.map((c) => ({ value: String(c.id), label: c.name }))]}
                onChange={(v) => {
                  const guest = Number(v) || 0
                  if (guest > 0) void post({ action: 'add-guest', guest_id: guest })
                }}
              />
            </div>
          </div>
        </section>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------------- */
/* piesele cutiei, pe grupuri                                                */
/* ------------------------------------------------------------------------- */

function Group({ group }: { group: ConfigGroup }) {
  return (
    <section className={`dcol dcol-span-${group.span}`} data-group={group.key}>
      <h3 className="dcol-head" title={group.lead}>
        <i className={`fas ${group.icon}`} aria-hidden="true"></i>
        <span className="dcol-name">{group.label}</span>
        <span className="dcol-count">{group.count}</span>
      </h3>
      <div className="device-config">
        {group.cards.map((card) => (
          <Card key={card.typeId} card={card} />
        ))}
      </div>
    </section>
  )
}

function Card({ card }: { card: ConfigCard }) {
  const [open, setOpen] = useState(false)

  return (
    <section className={`dcf-card${open ? ' is-open' : ''}`} data-type={card.typeId}>
      <header className="dcf-head" onClick={() => setOpen(!open)}>
        <span className="dcf-icon">
          <i className={`fas ${card.icon}`} aria-hidden="true"></i>
        </span>
        <span className="dcf-title">{card.title}</span>
        {card.count > 1 && <span className="dcf-count">{card.count}</span>}
        <i className="fas fa-chevron-down dcf-chevron" aria-hidden="true"></i>
      </header>

      <div className="dcf-summary">
        {card.lines.map((line, i) => (
          <div key={i} className="dcf-line" title={line.muted ? '' : line.text}>
            {line.muted ? <span className="dcf-muted">{line.text}</span> : line.text}
          </div>
        ))}
        {card.brief.length > 0 && (
          <dl className="dcf-brief">
            {card.brief.map((p) => (
              <Pair key={p.name} name={p.name} value={p.value} />
            ))}
          </dl>
        )}
        {card.more > 0 && <div className="dcf-line dcf-muted">+{card.more} more</div>}
      </div>

      <div className="dcf-details">
        {card.details.map((specs, i) => (
          <dl key={i} className="dcf-specs">
            {specs.map((spec, j) => (
              <Pair key={j} name={spec.name} value={spec.value} mark={spec.mark} />
            ))}
          </dl>
        ))}
      </div>
    </section>
  )
}

/* Un rand dt/dd; `–` mut pentru valoarea goala, `n/a` inclinat pentru cea NULL (component_check_size). */
function Pair({ name, value, mark = '' }: { name: string; value: string; mark?: '' | 'dash' | 'na' }) {
  return (
    <>
      <dt>{name}</dt>
      <dd>
        {mark === 'dash' ? <span className="dcf-muted">&ndash;</span> : mark === 'na' ? <em className="color-steel">n/a</em> : value}
      </dd>
    </>
  )
}
